import { useState } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Mail } from "lucide-react";

interface ContactProps {
  className?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Contact = ({ className }: ContactProps) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "done" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const { pathname } = useLocation();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setStatus("error");
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    setStatus("submitting");
    try {
      // Push to HubSpot tracker so the email is captured to the CRM.
      const _hsq = (window._hsq = window._hsq || []);
      _hsq.push(["identify", { email: trimmed }]);
      _hsq.push(["setPath", pathname]);
      _hsq.push(["trackPageView"]);
      _hsq.push([
        "trackCustomBehavioralEvent",
        { name: "email_capture_submit", properties: { source_path: pathname } },
      ]);
      setStatus("done");
    } catch (err) {
      console.error("Email capture error:", err);
      setStatus("error");
      setErrorMsg("Something went wrong. Please try again.");
    }
  };

  return (
    <section id="contact" className={cn("pt-0 pb-16 lg:pb-24 bg-muted/30", className)}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Contact Us
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              Drop your email — we'll send you the link and info.
            </p>
          </div>

          <div className="bg-card rounded-xl shadow-soft border border-cosmic-strong p-6 sm:p-8">
            {status === "done" ? (
              <div className="flex flex-col items-center text-center py-6">
                <CheckCircle2 className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">Thanks — you're on the list.</h3>
                <p className="text-muted-foreground">
                  We'll send the link and info to <span className="font-medium text-foreground">{email}</span> shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground">
                  Your email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
                  <Input
                    id="contact-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (status === "error") setStatus("idle");
                    }}
                    className="pl-11 h-12 rounded-full border-2"
                    disabled={status === "submitting"}
                  />
                </div>
                {status === "error" && (
                  <p className="text-sm text-destructive" role="alert">{errorMsg}</p>
                )}
                <Button
                  type="submit"
                  variant="cosmic"
                  className="w-full h-12 rounded-full"
                  disabled={status === "submitting"}
                >
                  {status === "submitting" ? "Sending..." : "Send me the link"}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  We'll only use your email to send the info you asked for.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
